import { useState } from 'react';
import { replace, toLower } from 'lodash';
import { Button, Icon } from '@repo/ui';
import { dateUtil } from '@xtnd/web/shared/utils';
import { ContactMethod } from '@repo/types';
import JoditEditor from 'jodit-react';

import { AttemptInvoice, MatterAttempt } from '../../types';
import { AttemptAttachmentsContainer } from '../common/AttemptAttachmentsContainer';
import { useMatterARContext } from '../common';

type Props = {
  isLoading: boolean;
  matterAttempts?: MatterAttempt[];
};

export default function ContactHistory({ matterAttempts, isLoading }: Props) {
  const [collapse, setCollapse] = useState<Record<string, boolean>>({});
  const { currentMatterId, currentClientId } = useMatterARContext();
  const [selectedAttempt, setSelectedAttempt] = useState<MatterAttempt | null>(null);
  const openAttachments = (attempt: MatterAttempt) => setSelectedAttempt(attempt);
  const closeAttachments = () => setSelectedAttempt(null);

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-6 border-t border-gray-100 dark:border-xtnd-dark-700 text-sm font-bold">
          <div className="flex items-center text-xtnd-blue dark:text-xtnd-light">
            <Icon name="LoadingSpinner" className="-ml-1 mr-3" />
            <span className="text-base font-bold">Collection attempts is loading...</span>
          </div>
        </div>
      )}

      {!isLoading &&
        (matterAttempts || []).map((attempt, index) => {
          if (
            attempt.contactMethod === ContactMethod.INTERNAL_EMAIL ||
            attempt.contactMethod === ContactMethod.EXTERNAL_EMAIL
          ) {
            return (
              <div
                key={`email-container ${index}`}
                className={`flex flex-col justify-between mt-4 p-4 border border-xtnd-light dark:border-xtnd-dark rounded-md min-h-fit text-xtnd-dark dark:text-xtnd-light`}
              >
                <header className="p-1">
                  <span className="flex justify-between">
                    <strong className="flex items-center gap-2">
                      <Icon name="HiOutlineMail" size="sm" /> Email
                    </strong>
                    <small>
                      {dateUtil.formatDate(
                        attempt.attemptDateTime,
                        attempt.contactMethod === ContactMethod.EXTERNAL_EMAIL
                          ? dateUtil.DateFormat.US_WITH_TIME
                          : dateUtil.DateFormat.US
                      )}
                    </small>
                  </span>

                  {attempt.emailInfo?.subject && (
                    <p>
                      <span>Subject: {attempt.emailInfo.subject}</span>
                    </p>
                  )}

                  {attempt.emailInfo?.toAddresses && (
                    <p>To: {attempt.emailInfo.toAddresses.map(x => x.email).join(', ')}</p>
                  )}

                  {attempt.emailInfo?.ccAddresses && (
                    <p>Cc: {attempt.emailInfo.ccAddresses.map(x => x.email).join(', ')}</p>
                  )}
                </header>

                {attempt.notesGlobal && (
                  <span
                    className="capitalize py-2 px-4"
                    dangerouslySetInnerHTML={{
                      __html: `${attempt.notesGlobal}`.replace(/\n/g, '<br />'),
                    }}
                  />
                )}

                {attempt.emailInfo?.emailBody && (
                  <main className="mt-2 rounded-lg py-2 flex flex-col gap-2 px-4">
                    <Button
                      variant="secondary"
                      onClick={() => setCollapse({ ...collapse, [index]: !collapse[index] })}
                    >
                      {`${collapse[index] ? 'Hide content' : 'View content'}`}
                    </Button>

                    {collapse[index] && (
                      <JoditEditor
                        value={attempt.emailInfo.emailBody}
                        config={{
                          readonly: false,
                          iframe: false,
                          toolbar: false,
                          toolbarSticky: false,
                          showXPathInStatusbar: false,
                          showCharsCounter: false,
                          showWordsCounter: false,
                          toolbarAdaptive: false,
                          removeButtons: [
                            'source',
                            'fullsize',
                            'about',
                            'spellcheck',
                            'hr',
                            'image',
                            'video',
                            'table',
                          ],
                          style: {
                            overflow: 'auto',
                          },
                        }}
                      />
                    )}
                  </main>
                )}

                <AttemptFooter attempt={attempt} onManage={openAttachments} />
              </div>
            );
          }

          return (
            <div
              key={`phone-container ${index}`}
              className={`cursor-default flex flex-col justify-between mt-4 p-4 border border-xtnd-light dark:border-xtnd-dark rounded-md max-h-80 text-xtnd-dark dark:text-xtnd-light min-h-fit`}
            >
              <header className="p-1 flex flex-col gap-1">
                <span className="flex justify-between">
                  <strong className="flex items-center gap-2">
                    <Icon name="FiPhone" size="sm" /> Phone
                  </strong>
                  <small>
                    {dateUtil.formatDate(attempt.attemptDateTime, dateUtil.DateFormat.US)}
                  </small>
                </span>

                {attempt.notesGlobal && (
                  <span
                    className="capitalize py-2 px-4"
                    dangerouslySetInnerHTML={{
                      __html: `${attempt.notesGlobal}`.replace(/\n/g, '<br />'),
                    }}
                  />
                )}
              </header>

              <AttemptFooter attempt={attempt} onManage={openAttachments} />
            </div>
          );
        })}

      {selectedAttempt && currentMatterId && currentClientId && (
        <AttemptAttachmentsContainer
          isOpen={!!selectedAttempt}
          onClose={closeAttachments}
          matterId={currentMatterId}
          clientId={currentClientId}
          attempt={selectedAttempt}
        />
      )}
    </>
  );
}

const AttemptFooter = ({
  attempt,
  onManage,
}: {
  attempt: MatterAttempt;
  onManage: (attempt: MatterAttempt) => void;
}) => {
  return (
    <>
      {attempt.invoiceDetails && attempt.invoiceDetails.length > 0 && (
        <InvoiceDetails invoiceDetails={attempt.invoiceDetails} />
      )}

      <Button
        variant="secondary"
        size="sm"
        onClick={() => onManage(attempt)}
        className="mt-2 self-start"
      >
        <Icon name="HiOutlinePaperClip" size="sm" />
        Attachments
      </Button>

      <footer className="mt-2 flex flex-col">
        <small>
          Next follow-up:{' '}
          {attempt.nextFollowUp
            ? dateUtil.formatDate(attempt.nextFollowUp, dateUtil.DateFormat.US)
            : ''}
        </small>

        <small>Attempted by: {attempt.attemptedBy ?? ''}</small>
      </footer>
    </>
  );
};

const InvoiceDetails = ({ invoiceDetails }: { invoiceDetails: AttemptInvoice[] }) => (
  <div className="rounded-lg flex flex-col gap-2 mt-4 px-4">
    {invoiceDetails.map(inv => (
      <div
        key={inv.invoiceId}
        className="border border-xtnd-light dark:border-xtnd-dark rounded-lg shadow-sm"
      >
        <div className="w-full flex items-center gap-4 px-4 py-3 text-left">
          <Icon name="FaCircle" size="xs" />
          <div className="flex items-center gap-4">
            <div className="font-medium">Invoice #{inv.invoiceNumber ?? ''}</div>
            {inv.paymentOutcome && (
              <div className="relative pl-4 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-4 before:w-px before:bg-gray-300">
                <span className="font-medium capitalize">
                  Payment Outcome: {replace(toLower(inv.paymentOutcome), /_/g, ' ')}{' '}
                </span>
              </div>
            )}

            {inv.contactAttempt && (
              <div className="relative pl-4 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-4 before:w-px before:bg-gray-300">
                <span className="font-medium capitalize">
                  Contact Attempt: {replace(toLower(inv.contactAttempt), /_/g, ' ')}{' '}
                </span>
              </div>
            )}
            {inv.administrativeFlag && (
              <div className="relative pl-4 before:content-[''] before:absolute before:left-0 before:top-1/2 before:-translate-y-1/2 before:h-4 before:w-px before:bg-gray-300">
                <span className="font-medium capitalize">
                  Administrative: {replace(toLower(inv.administrativeFlag), /_/g, ' ')}{' '}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>
    ))}
  </div>
);
